import React, { useState, useEffect } from "react";
import { FaEdit, FaTimesCircle, FaCheckCircle } from "react-icons/fa";
import { useSelector } from "react-redux";
import axios from "axios";
import {
  getLookupName,
  getLookupValue,
  addLookupValue,
  updateLookupValue,
  deleteLookupValue,
} from "../../server/Api";

const TecogisAdmin = () => {
  // State for form inputs
  const [lookupNames, setLookupNames] = useState([]);
  const [selectedLookupName, setSelectedLookupName] = useState("");
  const [lookupValueName, setLookupValueName] = useState("");
  const [lookupValueOrder, setLookupValueOrder] = useState(1);
  const [lookupValueCode, setLookupValueCode] = useState("");
  const [enteredBy, setEnteredBy] = useState("Tecogis Admin");
  const [selectedLookupId, setSelectedLookupId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const selectedEvent = useSelector((state) => state.auth.selectedEvent);

  // State for storing lookup values based on selected lookup_name
  const [lookupValues, setLookupValues] = useState([]);

  // State for error message
  const [errorMessage, setErrorMessage] = useState("");

  // State to track which lookup value is being edited
  const [editingLookupValue, setEditingLookupValue] = useState(null);

  // State for showing confirmation dialog for delete
  const [deleteConfirmation, setDeleteConfirmation] = useState({
    isOpen: false,
    lookupValueId: null,
    lookupValueName: "",
  });

  const user = useSelector((state) => state.auth.user);
  // Fetch lookup names from the API
  useEffect(() => {
    axios
      .get(getLookupName())
      .then((response) => {
        setLookupNames(response.data);
      })
      .catch((error) => {
        console.error("Error fetching lookup names:", error);
      });
  }, []);

  // Fetch lookup values when a lookup name is selected
  useEffect(() => {
    if (selectedLookupId) {
      axios
        .get(`${getLookupValue()}?Lookup_Id=${selectedLookupId}`)
        .then((response) => {
          const values = response.data.data;
          setLookupValues(values);

          // Only set order if not editing
          if (!editingLookupValue) {
            const filteredValues = values.filter(
              (v) => v.Lookup_Id === selectedLookupId
            );
            const maxOrder = filteredValues.reduce(
              (max, val) => Math.max(max, val.Lookup_Value_Order || 0),
              0
            );
            setLookupValueOrder(maxOrder + 1);
          }
        })
        .catch((error) => {
          console.error("Error fetching lookup values:", error);
        });
    }
  }, [selectedLookupId]);

  // Handle form submission for creating or updating a lookup value
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check if the Lookup_Value_Name already exists
    const isDuplicate = lookupValues.some(
      (value) =>
        value.Lookup_Value_Name.toLowerCase() ===
          lookupValueName.toLowerCase() &&
        value.Lookup_Value_Id !==
          (editingLookupValue ? editingLookupValue.Lookup_Value_Id : null)
    );

    if (isDuplicate) {
      setErrorMessage("Lookup value already exists");
      return; // Prevent saving if duplicate found
    }

    setErrorMessage(""); // Clear the error message if no duplicate

    const payload = {
      eventName: user.eventName, // Directly use the value from Redux
      Lookup_Value_Name: lookupValueName,
      Lookup_Value_Code: lookupValueCode,
      Lookup_Value_Order: lookupValueOrder,
      Lookup_Id: selectedLookupId, // From the selected dropdown
      Lookup_Name: selectedLookupName, // From the selected dropdown
      enteredBy: enteredBy,
      modifiedBy: user.name, // Directly use the name from Redux
    };

    try {
      let response;
      if (editingLookupValue) {
        response = await axios.put(
          `${updateLookupValue()}/${editingLookupValue.Lookup_Value_Id}`,
          payload
        );
      } else {
        response = await axios.post(addLookupValue(), payload);
      }

      const updatedLookupValue = response.data.data;

      // Update the table data
      const updatedLookupValues = editingLookupValue
        ? lookupValues.map((value) =>
            value.Lookup_Value_Id === updatedLookupValue.Lookup_Value_Id
              ? updatedLookupValue
              : value
          )
        : [...lookupValues, updatedLookupValue];

      // Sort by order
      updatedLookupValues.sort(
        (a, b) => a.Lookup_Value_Order - b.Lookup_Value_Order
      );

      // Update state
      setLookupValues(updatedLookupValues);

      // Reset form fields except order, which should be max + 1
      setLookupValueName("");
      setLookupValueCode("");
      setEditingLookupValue(null);

      // Calculate max order for selected lookup and set order to max + 1
      const filteredValues = updatedLookupValues.filter(
        (v) => v.Lookup_Id === selectedLookupId
      );
      const maxOrder = filteredValues.reduce(
        (max, val) => Math.max(max, val.Lookup_Value_Order || 0),
        0
      );
      setLookupValueOrder(maxOrder + 1);
    } catch (error) {
      console.error("Error posting or updating lookup value:", error);
    }
  };

  // Handle edit button click
  const handleEdit = (value) => {
    setEditingLookupValue(value);
    setSelectedLookupName(value.Lookup_Name);
    setLookupValueName(value.Lookup_Value_Name);
    setLookupValueOrder(value.Lookup_Value_Order);
    setLookupValueCode(value.Lookup_Value_Code);
  };

  // Handle delete button click
  const handleDelete = (value) => {
    setDeleteConfirmation({
      isOpen: true,
      lookupValueId: value.Lookup_Value_Id,
      lookupValueName: value.Lookup_Value_Name,
    });
  };

  // Confirm delete action
  const confirmDelete = async () => {
    try {
      await axios.delete(
        `${deleteLookupValue()}/${deleteConfirmation.lookupValueId}`
      );

      // Remove the deleted lookup value from the table
      setLookupValues(
        lookupValues.filter(
          (value) => value.Lookup_Value_Id !== deleteConfirmation.lookupValueId
        )
      );

      // Close the confirmation modal
      setDeleteConfirmation({
        isOpen: false,
        lookupValueId: null,
        lookupValueName: "",
      });
    } catch (error) {
      console.error("Error deleting lookup value:", error);
    }
  };

  // Cancel delete action
  const cancelDelete = () => {
    setDeleteConfirmation({
      isOpen: false,
      lookupValueId: null,
      lookupValueName: "",
    });
  };

  return (
    <div className="container-fluid">
      <div className="row mb-20">
        <div class="occasion-top d-flex justify-content-between  align-items-center">
          <h2 className="mb-0">Lookup</h2>
          <div style={{ maxWidth: "300px", width: "100%" }}>
            <input
              type="text"
              className="form-control mb-3"
              placeholder="Search Lookup..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
      </div>
      <div className="row g-4">
        <div className="col-lg-6">
          <div className="form-section">
            <form>
              <div className="row">
                <div className="col-md-4">
                  <label className="form-label">Lookup Name</label>
                  <select
                    className="form-select"
                    id="floatingInput"
                    value={selectedLookupName}
                    onChange={(e) => {
                      setSelectedLookupName(e.target.value);
                      const selectedLookup = lookupNames.find(
                        (lookup) => lookup.lookup_name === e.target.value
                      );
                      setSelectedLookupId(
                        selectedLookup ? selectedLookup.lookup_mst_id : null
                      );
                    }}
                  >
                    <option value="">Select Lookup Name</option>
                    {(user.isSuperAdmin
                      ? lookupNames
                      : lookupNames.filter(
                          (lookup) => lookup.lookup_type === "C"
                        )
                    ).map((lookup) => (
                      <option
                        key={lookup.lookup_mst_id}
                        value={lookup.lookup_name}
                      >
                        {lookup.lookup_name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="form-label">Lookup Value</label>
                  <input
                    type="text"
                    className="form-control"
                    id="floatingPassword"
                    value={lookupValueName}
                    onChange={(e) => setLookupValueName(e.target.value)}
                    placeholder="Lookup Value"
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Order</label>
                  <input
                    type="number"
                    className="form-control"
                    id="floatingPassword"
                    value={lookupValueOrder}
                    onChange={(e) => setLookupValueOrder(e.target.value)}
                    placeholder="Order"
                  />
                </div>
              </div>
              <div className="mt-4 d-flex justify-content-end gap-2 justify-content-center py-5">
                <button
                  className="btn btn-primary btn-save"
                  onClick={handleSubmit}
                >
                  {editingLookupValue ? "Update" : "Save"}
                </button>
                <button
                  className="btn btn-secondary btn-cancel"
                  onClick={() => {
                    setEditingLookupValue(null);

                    // Reset form fields
                    setLookupValueName("");
                    setLookupValueCode("");

                    // 👇 Set Order to last + 1 for current selected Lookup Id
                    const filteredValues = lookupValues.filter(
                      (v) => v.Lookup_Id === selectedLookupId
                    );
                    const maxOrder = filteredValues.reduce(
                      (max, val) => Math.max(max, val.Lookup_Value_Order || 0),
                      0
                    );
                    setLookupValueOrder(maxOrder + 1);
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
        <div className="col-lg-6">
          <div className="border rounded shadow bg-white px-3 py-3">
            <div className="table-responsive">
              <table className="table table-bordered table-hover">
                <thead className="thead-dark table-bg-new">
                  <tr>
                    <th>Sr No</th>
                    <th>Lookup Name</th>
                    <th>Lookup Value</th>
                    <th>Order</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {lookupValues.length > 0 ? (
                    lookupValues
                      .filter(
                        (value) =>
                          value.Lookup_Id === selectedLookupId &&
                          (value.Lookup_Name.toLowerCase().includes(
                            searchTerm.toLowerCase()
                          ) ||
                            value.Lookup_Value_Name.toLowerCase().includes(
                              searchTerm.toLowerCase()
                            ))
                      )
                      .map((value, index) => (
                        <tr key={value.Lookup_Value_Id}>
                          <td>{index + 1}</td>
                          <td>{value.Lookup_Name}</td>
                          <td>{value.Lookup_Value_Name}</td>
                          <td>{value.Lookup_Value_Order}</td>
                          <td className="event-actions">
                            <a href="#" class="btn edit-btn" onClick={() => handleEdit(value)}>Edit</a>
                            {/* <FaEdit
                              onClick={() => handleEdit(value)}
                              className="me-2"
                              title="Edit"
                            /> */}
                            <a href="#" class="btn btn-danger" onClick={() => handleDelete(value)}>Delete</a>
                            {/* <FaTimesCircle
                              onClick={() => handleDelete(value)}
                              title="Remove"
                            /> */}
                          </td>
                        </tr>
                      ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="text-center">
                        No lookup values found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* <div className='col-lg-3'>
          <div className='form-section'>
            <h5>Test</h5>
          </div>
        </div> */}
      </div>

      {errorMessage && (
        <div className="row mt-20">
          <div className="col-xl-8">
            <div className="alert alert-danger">{errorMessage}</div>
          </div>
        </div>
      )}

      {/* <div className="row mt-20">
        <div className="col-xl-8">
          <button className="btn btn-primary mr-30" onClick={handleSubmit}>
            {editingLookupValue ? 'Update' : 'Save'}
          </button>
          <button className="btn btn-secondary" onClick={() => setEditingLookupValue(null)}>
            Cancel
          </button>
        </div>
      </div> */}

{deleteConfirmation.isOpen && (
  <div
    className="modal fade show d-block"
    tabIndex="-1"
    style={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}
  >
    <div className="modal-dialog modal-dialog-centered">
      <div className="modal-content">
        <div className="modal-header">
          <h5 className="modal-title">Confirm Delete</h5>
          <button
            type="button"
            className="btn-close"
            onClick={cancelDelete}
          ></button>
        </div>
        <div className="modal-body">
          <p>
            Are you sure you want to delete "
            <strong>{deleteConfirmation.lookupValueName}</strong>"?
          </p>
        </div>
        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-secondary cancel-btn"
            onClick={cancelDelete}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-danger"
            onClick={confirmDelete}
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  </div>
)}

    </div>
  );
};

export default TecogisAdmin;
